import { layoutConfig } from 'utils/layout';

// Target width for a single participant tile when content is pinned (SAMBA-9138).
const MIN_TILE_WIDTH = 160;

/**
 * Splits the room width between the pinned content panel and the participant-tile strip.
 *
 * The strip gets a fixed reserve wide enough for one comfortable tile plus the pagination arrows
 * (`minSidebarWidth` already carries that gutter), capped just below the point where the grid would
 * add a second column, so tiles stay a single wide column on narrow/zoomed screens (SAMBA-9138).
 * The reserve is independent of `containerWidth` so the strip doesn't jump when a side panel
 * animates the available width — content yields via `maxContentWidth` instead.
 */
export const getPinnedContentBounds = ({
  containerWidth,
  hasTiles,
  isPiP,
}: {
  containerWidth: number;
  hasTiles: boolean;
  isPiP: boolean;
}) => {
  const { minSidebarWidth, minFeasibleWidth, minContentWidth } = layoutConfig;

  const paginationGutter = minSidebarWidth - minFeasibleWidth;
  // fitToRenderArea adds a second column at 2 * minFeasibleWidth; stay just under it.
  const singleColumnMaxReserve = 2 * minFeasibleWidth - 1;

  const tilesReserve = hasTiles
    ? Math.min(MIN_TILE_WIDTH + paginationGutter, singleColumnMaxReserve)
    : minSidebarWidth;

  const maxContentWidth = containerWidth - tilesReserve;

  // Content keeps its usual floor but yields toward the minimum when that is the only way to honour
  // the tile reserve (otherwise react-resizable's min constraint would push width back over the cap).
  const minPanelWidth = Math.min(
    Math.max(containerWidth * minContentWidth, isPiP ? 300 : 420),
    maxContentWidth
  );

  return { tilesReserve, maxContentWidth, minPanelWidth };
};
